import { Text } from '@eo-locale/react';
import { FC, useCallback, useEffect, useState } from 'react';
import { FormProvider, SubmitHandler, useForm, useWatch } from 'react-hook-form';
import { useMutation } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { ContactApi, ErrorResponse } from '../../api';
import { RoutePath } from '../../config';
import { useApiErrorHandler, useModal } from '../../hooks';
import { Button, Input, Textarea } from '../atoms';
import styles from './ContactForm.module.css';
import { Modal } from './Modal';

interface FormFields {
  content: string;
  title: string;
}

export const ContactForm: FC = () => {
  const navigate = useNavigate();
  const modal = useModal();
  const { error, mutate, isSuccess } = useMutation<
    void,
    ErrorResponse,
    FormFields
  >(({ content, title }) => {
    return ContactApi.send(title, content);
  }, {});

  const form = useForm<FormFields>({
    defaultValues: {
      title: '',
      content: '',
    },
  });

  const keyMapper = useCallback(() => 'content' as keyof FormFields, []);
  const handleApiError = useApiErrorHandler(form.setError, keyMapper);

  useEffect(() => {
    handleApiError(error);
  }, [error, handleApiError]);

  useEffect(() => {
    if (isSuccess) {
      modal.current?.open();
    }
  }, [isSuccess, modal]);

  const onSubmit: SubmitHandler<FormFields> = async data => {
    mutate(data);
  };

  // --- LICZNIKI ZNAKÓW ---
  const titleValue = useWatch({ control: form.control, name: 'title' }) ?? '';
  const contentValue = useWatch({ control: form.control, name: 'content' }) ?? '';

  const TITLE_MAX = 100;
  const CONTENT_MAX = 1000;

  const titleCount = titleValue.length;
  const contentCount = contentValue.length;

  return (
    <FormProvider {...form}>
      <Modal ref={modal}>
        <p className="text-center">
          <Text id="contact:success" />
        </p>
        <footer className="text-center" style={{ marginTop: 24 }}>
          <Button
            onClick={() => {
              navigate(RoutePath.Profile);
            }}>
            Ok
          </Button>
        </footer>
      </Modal>

      <form
        noValidate
        className={styles.container}
        onSubmit={form.handleSubmit(onSubmit)}>

        {/* --- Tytuł --- */}
        <div className={styles.fieldWrapper}>
          <Input
            block
            required
            requiredMark={null}
            className={styles.field}
            label="contact:message_title"
            name="title"
            maxLength={TITLE_MAX}
          />
          <div
            className={`${styles.counter} ${
              titleCount > TITLE_MAX ? styles.counterError : ''
            }`}>
            {titleCount}/{TITLE_MAX}
          </div>
        </div>

        {/* --- Treść --- */}
        <div className={styles.fieldWrapper}>
          <Textarea
            block
            required
            requiredMark={null}
            label="contact:message_content"
            name="content"
            rows={5}
            maxLength={CONTENT_MAX}
          />
          <div
            className={`${styles.counter} ${
              contentCount > CONTENT_MAX ? styles.counterError : ''
            }`}>
            {contentCount}/{CONTENT_MAX}
          </div>
        </div>

        <footer className={styles.footer}>
          <Button type="submit" disabled={titleCount === 0 || contentCount === 0}>
            <Text id="contact:submit" />
          </Button>
        </footer>
      </form>
    </FormProvider>
  );
};